import React, { useEffect, useState } from "react";
import { Card, Button, Input, message, Alert, Space, Segmented, Typography } from "antd";
import axios, { API_BASE_URL } from "../api/axios";
import { PLANNING_MODES, getPlanningModeLabels } from "../utils/planningMode";

const { Text } = Typography;

const LS_TOURS_KEY = "routing.cachedTours";
const LS_REPORT_CTX_KEY = "routing.cachedReportContext";
const LS_RUN_STATE_KEY = "routing.runState";

const COLORS = {
  primary: "#3FB6C2",
  primaryDark: "#1F4E57",
  primaryLight: "#E8F7F9",
  border: "#DCE7F3",
  text: "#1F2937",
  muted: "#6B7280",
};

const AccountPage = () => {
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNewConfirm, setPwNewConfirm] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState("");

  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteCode, setDeleteCode] = useState("");
  const [delLoading, setDelLoading] = useState(false);
  const [planningMode, setPlanningMode] = useState(PLANNING_MODES.OWN_FLEET);
  const [modeLoading, setModeLoading] = useState(false);

  useEffect(() => {
    axios
      .get("/users/me")
      .then((res) => {
        const mode = res.data?.planningMode || PLANNING_MODES.OWN_FLEET;
        setPlanningMode(mode);
        localStorage.setItem("user", JSON.stringify(res.data));
        window.dispatchEvent(new Event("planning-mode-updated"));
      })
      .catch(() => {});
  }, []);

  const handlePlanningModeChange = async (nextMode) => {
    setModeLoading(true);

    try {
      const res = await axios.put("/users/me/planning-mode", {
        planningMode: nextMode,
      });
      setPlanningMode(res.data?.planningMode || nextMode);
      localStorage.setItem("user", JSON.stringify(res.data));
      localStorage.removeItem(LS_TOURS_KEY);
      localStorage.removeItem(LS_REPORT_CTX_KEY);
      localStorage.removeItem(LS_RUN_STATE_KEY);
      window.dispatchEvent(new Event("planning-mode-updated"));
      message.success("Planungsmodus gespeichert.");
    } catch {
      message.error("Planungsmodus konnte nicht gespeichert werden.");
    } finally {
      setModeLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (pwNew !== pwNewConfirm) {
      setPwSuccess(false);
      setPwError("Die neuen Passwörter stimmen nicht überein.");
      message.error("Die neuen Passwörter stimmen nicht überein.");
      return;
    }

    setPwLoading(true);
    setPwSuccess(false);
    setPwError("");

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/auth/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ oldPassword: pwOld, newPassword: pwNew }),
        }
      );

      if (res.ok) {
        setPwSuccess(true);
        setPwOld("");
        setPwNew("");
        setPwNewConfirm("");
        message.success("Passwort erfolgreich geändert!");
      } else {
        const err = await res.text();
        setPwError(err || "Ändern fehlgeschlagen.");
        message.error(err || "Passwortänderung fehlgeschlagen.");
      }
    } catch (err) {
      setPwError("Netzwerkfehler");
      message.error("Netzwerkfehler");
    }

    setPwLoading(false);
  };

  const requestDelete = async () => {
    setDelLoading(true);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/auth/request-delete`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.ok) {
        message.success("Bestätigungscode gesendet. Prüfe deine E-Mail.");
        setDeleteStep(2);
      } else {
        const err = await res.text();
        message.error(err);
      }
    } catch (err) {
      message.error("Netzwerkfehler");
    }

    setDelLoading(false);
  };

  const confirmDelete = async () => {
    setDelLoading(true);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/auth/confirm-delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ code: deleteCode }),
        }
      );

      if (res.ok) {
        message.success("Account gelöscht.");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      } else {
        const err = await res.text();
        message.error(err);
      }
    } catch (err) {
      message.error("Netzwerkfehler");
    }

    setDelLoading(false);
  };

  return (
    <div className="account-page">
      <h2 className="app-page-title">Account-Einstellungen</h2>

      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        <Card
          title="Planungsmodus"
          style={{
            borderRadius: 16,
            border: `1px solid ${COLORS.border}`,
            boxShadow: "0 8px 24px rgba(31, 78, 87, 0.06)",
          }}
        >
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Segmented
              block
              value={planningMode}
              disabled={modeLoading}
              onChange={handlePlanningModeChange}
              options={[
                {
                  label: "Eigenfuhrpark",
                  value: PLANNING_MODES.OWN_FLEET,
                },
                {
                  label: "Ausschreibung",
                  value: PLANNING_MODES.TENDER,
                },
              ]}
            />

            <Alert
              type={planningMode === PLANNING_MODES.TENDER ? "info" : "success"}
              showIcon
              message={`Aktiv: ${getPlanningModeLabels(planningMode).modeName}`}
              description={
                planningMode === PLANNING_MODES.TENDER
                  ? "Die Fahrzeugseite arbeitet mit virtuellen Fahrzeugprofilen und Standortannahmen fuer Ausschreibungen."
                  : "Die Fahrzeugseite arbeitet mit eigenen Bussen/Fahrzeugen und realen Depots."
              }
            />

            <Text style={{ color: COLORS.muted }}>
              Die Optimierung nutzt weiterhin dieselbe Kapazitätslogik. Der Modus
              steuert, ob die Einheiten fachlich als eigene Fahrzeuge oder als
              Ausschreibungsprofile behandelt werden.
            </Text>
          </Space>
        </Card>

        <Card
          title="Passwort ändern"
          style={{
            borderRadius: 16,
            border: `1px solid ${COLORS.border}`,
            boxShadow: "0 8px 24px rgba(31, 78, 87, 0.06)",
          }}
        >
          {pwSuccess && (
            <Alert
              type="success"
              message="Passwort wurde erfolgreich geändert."
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {pwError && (
            <Alert
              type="error"
              message={pwError}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Input.Password
            placeholder="Altes Passwort"
            value={pwOld}
            onChange={(e) => setPwOld(e.target.value)}
            size="large"
            style={{ marginBottom: 12 }}
          />

          <Input.Password
            placeholder="Neues Passwort"
            value={pwNew}
            onChange={(e) => setPwNew(e.target.value)}
            size="large"
            style={{ marginBottom: 12 }}
          />

          <Input.Password
            placeholder="Neues Passwort bestätigen"
            value={pwNewConfirm}
            onChange={(e) => setPwNewConfirm(e.target.value)}
            onPressEnter={handleChangePassword}
            status={pwNewConfirm && pwNew !== pwNewConfirm ? "error" : undefined}
            size="large"
            style={{ marginBottom: 12 }}
          />

          <Button
            type="primary"
            onClick={handleChangePassword}
            loading={pwLoading}
            block
            size="large"
            disabled={!pwOld || !pwNew || !pwNewConfirm || pwLoading}
          >
            Passwort ändern
          </Button>
        </Card>

        <Card
          title="Account löschen"
          style={{
            borderRadius: 16,
            border: "1px solid #ffccc7",
            boxShadow: "0 8px 24px rgba(31, 78, 87, 0.06)",
          }}
        >
          {deleteStep === 1 && (
            <>
              <Alert
                type="warning"
                message="Achtung: Dieser Vorgang kann nicht rückgängig gemacht werden!"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <div
                style={{
                  background: "#FFFBE6",
                  color: "#8A6D00",
                  border: "1px solid #FFE58F",
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 16,
                  lineHeight: 1.45,
                }}
              >
                <b>Hinweis:</b> Die Zustellung der E-Mail mit dem Lösch-Code
                kann bis zu <b>5–10 Minuten</b> dauern.
                <br />
                Bitte prüfe auch deinen <b>Spam-Ordner</b>.
              </div>

              <Button danger block onClick={requestDelete} loading={delLoading}>
                Account-Lösch-Code anfordern
              </Button>
            </>
          )}

          {deleteStep === 2 && (
            <>
              <Alert
                type="warning"
                message="Du hast einen Code per E-Mail erhalten. Gib ihn ein, um deinen Account zu löschen."
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Input
                placeholder="Bestätigungscode"
                value={deleteCode}
                onChange={(e) => setDeleteCode(e.target.value)}
                size="large"
                style={{ marginBottom: 16 }}
              />

              <Button
                danger
                block
                onClick={confirmDelete}
                loading={delLoading}
                disabled={!deleteCode}
              >
                Account unwiderruflich löschen
              </Button>
            </>
          )}
        </Card>
      </Space>

      <style>{`
        .account-page {
          max-width: 520px;
          margin: 0 auto;
        }

        .account-page h2 {
          margin-top: 0;
          margin-bottom: 18px;
          color: ${COLORS.text};
          font-weight: 700;
        }

        .account-page .ant-card-head-title {
          color: ${COLORS.text};
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default AccountPage;
